import { FC, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

import './GoogleCallback.scss';
import {
  getWeekEdgesDateOf,
  googleCalAuth,
  captureGoogleAuthError,
  useAuthContext,
  useSyncGCalendarsList,
  generalSnackBarAtom,
  useSyncGoogleCalendarEvents,
  useSubscribeGoogleCalendarWatch,
} from '../../../data-access';
import { Path } from '../../../routing';
import { useRecoilState } from 'recoil';

const GoogleCallback: FC = () => {
  const location = useLocation();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { syncGCalendarsList } = useSyncGCalendarsList();
  const { subscribeGoogleCalendarWatch } = useSubscribeGoogleCalendarWatch();
  const { syncGoogleCalEvents } = useSyncGoogleCalendarEvents();
  const [_, setGeneralSnackbar] = useRecoilState(generalSnackBarAtom);

  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code') ?? '';

  useEffect(() => {
    if (!user.userId || !code) {
      return;
    }

    const update = async () => {
      let redirectUrl = Path.DASHBOARD;
      try {
        const cred = await googleCalAuth.signinCallback(window.location.href);
        if (cred) {
          redirectUrl = (cred.url_state as string) || Path.DASHBOARD;
          const scopes = cred.scopes;
          const newCalendarIds = await syncGCalendarsList({
            userId: user.userId,
            rootEmail: cred.profile.email,
            accessToken: cred.access_token,
            refreshToken: cred.refresh_token,
            expiryDate: dayjs.unix(cred.expires_at).toISOString(),
            scopes,
          });
          const { startDate, endDate } = getWeekEdgesDateOf(dayjs());

          syncGoogleCalEvents({
            userId: user.userId,
            calendarIds: newCalendarIds,
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString(),
          });
          subscribeGoogleCalendarWatch({
            calendarIds: newCalendarIds,
            userId: user.userId,
          });
        }
      } catch (error) {
        captureGoogleAuthError(error, 'calendar_connect', 'callback');
        if (error instanceof Error && error.message.includes('No matching state found')) {
          setGeneralSnackbar(
            'Something went wrong — make sure cookies are enabled and try again.'
          );
        } else {
          setGeneralSnackbar('Something went wrong — try again?');
        }
      } finally {
        navigate(redirectUrl, { replace: true });
      }
    };

    update();
  }, [user?.userId, code]);

  return (
    <div className="google-callback-page">
      <CircularProgress />
    </div>
  );
};

export default GoogleCallback;
